<template>
  <AriModal v-if="visible" width="640px" :showClose="false" :modalTitle="$t('billingAddress.title')">
    <template #body>
      <div class="flex flex-col gap-default m-xs">
        <div class="nf525-banner">
          <div class="nf525-banner__title font-semibold">{{ $t('billingAddress.nf525Title') }}</div>
          <div class="text-secondary">{{ $t('billingAddress.nf525Banner') }}</div>
        </div>
        <div class="section-title font-semibold">{{ $t('billingAddress.requiredSection') }}</div>
        <el-form ref="formRef" :model="billingForm" :rules="rules" label-position="top" hide-required-asterisk @submit.prevent>
          <div class="bloc-city">
            <el-form-item class="bloc-city__field" :label="$t('postalCode') + ' *'" prop="postalCode">
              <el-input v-model="billingForm.postalCode" maxlength="10" />
            </el-form-item>
            <el-form-item class="bloc-city__field" :label="$t('country') + ' *'" prop="country">
              <countrySelector v-model="billingForm.country" :placeholder="$t('country')" />
            </el-form-item>
            <el-form-item class="bloc-city__field" :label="$t('city') + ' *'" prop="city">
              <el-select v-if="cityOptions.length > 1" v-model="billingForm.city" class="w-full" filterable :placeholder="$t('billingAddress.cityPlaceholder')">
                <el-option v-for="city in cityOptions" :key="city" :label="city" :value="city" />
              </el-select>
              <el-input v-else v-model="billingForm.city" maxlength="40" />
            </el-form-item>
          </div>
          <el-form-item :label="$t('adr1')" prop="adr1">
            <el-autocomplete
              v-model="billingForm.adr1"
              class="w-full"
              maxlength="250"
              :fetch-suggestions="queryAddress"
              value-key="adr1"
              :trigger-on-focus="false"
              @select="onSelectAddress">
              <template #default="{ item }">
                <span>{{ item.label }}</span>
              </template>
            </el-autocomplete>
          </el-form-item>
          <el-form-item v-if="member && member.adr2" :label="$t('billingAddress.adr2')" prop="adr2">
            <el-input v-model="billingForm.adr2" maxlength="250" />
          </el-form-item>
          <el-form-item v-if="member && member.adr3" :label="$t('billingAddress.adr3')" prop="adr3">
            <el-input v-model="billingForm.adr3" maxlength="250" />
          </el-form-item>
          <div v-if="member && member.canRefuseAddress" class="flex items-center gap-xs">
            <el-switch v-model="billingForm.addressRefused" />
            <span>{{ $t('billingAddress.refuse') }}</span>
          </div>
        </el-form>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-between flex-col sm:flex-row gap-lg">
        <AriButton text :content="$t('cancel')" @click="onCancel" />
        <AriButton filled :content="$t('confirm')" @click="onConfirm" />
      </div>
    </template>
  </AriModal>
</template>

<script>
import countrySelector from '@/core/components/countrySelector.vue';

const EMPTY_FORM = { postalCode: '', city: '', country: '', adr1: '', adr2: '', adr3: '', addressRefused: false };

export default {
  name: 'modalBillingAddress',
  components: { countrySelector },
  props: {
    visible: { type: Boolean, default: false },
    member: { type: Object, default: () => ({}) },
    // zone country used as fallback to prefill the country when the member doesn't have one
    zoneCountry: { type: String, default: null }
  },
  emits: ['saved', 'cancel'],
  data () {
    return {
      saving: false,
      // becomes true after the first confirm attempt : gates the re-validation on checkbox toggle
      submitted: false,
      cityOptions: [],
      billingForm: { ...EMPTY_FORM },
      lastLookedUpPostalCode: null
    };
  },
  computed: {
    rules () {
      if (!this.submitted) return {};
      const t = this.$t;
      // adr1 is required for B2B and for B2C members who did not refuse to share their address
      const refused = this.member?.canRefuseAddress && this.billingForm.addressRefused;
      const adr1Rules = [{ max: 250, message: t('billingAddress.errors.adr1Max'), trigger: 'change' }];
      if (!refused) {
        adr1Rules.unshift(
          { required: true, message: t('billingAddress.errors.adr1'), trigger: 'change' },
          { min: 5, message: t('billingAddress.errors.adr1'), trigger: 'change' }
        );
      }
      return {
        postalCode: [
          { required: true, message: t('billingAddress.errors.postalCode'), trigger: 'change' },
          { min: 3, message: t('billingAddress.errors.postalCode'), trigger: 'change' },
          { max: 10, message: t('billingAddress.errors.postalCodeMax'), trigger: 'change' }
        ],
        city: [
          { required: true, message: t('billingAddress.errors.cityRequired'), trigger: 'change' },
          { min: 2, message: t('billingAddress.errors.city'), trigger: 'change' },
          { max: 40, message: t('billingAddress.errors.cityMax'), trigger: 'change' }
        ],
        country: [
          { required: true, message: t('billingAddress.errors.country'), trigger: 'change' }
        ],
        adr1: adr1Rules
      };
    }
  },
  watch: {
    visible (isVisible) {
      if (isVisible) this.initForm();
    },
    'billingForm.addressRefused' () {
      // re-validate once a submit has already been attempted, so the adr1 error clears when the
      // address becomes optional and re-appears if the member unchecks the box
      if (this.submitted) this.$nextTick(() => this.$refs.formRef?.validateField('adr1'));
    },
    'billingForm.postalCode' (postalCode) {
      // initForm() already triggered a lookup for the same value when the modal opened : the
      // reassignment of billingForm triggers it too, so skip the duplicate call
      if (postalCode === this.lastLookedUpPostalCode) {
        this.lastLookedUpPostalCode = null;
        return;
      }
      this.prefillCityFromPostalCode(postalCode);
    }
  },
  methods: {
    initForm () {
      const member = this.member || {};
      this.submitted = false;
      this.billingForm = {
        postalCode: member.postalCode || '',
        city: member.city || '',
        country: member.country || this.zoneCountry || '',
        adr1: member.adr1 || '',
        adr2: member.adr2 || '',
        adr3: member.adr3 || '',
        addressRefused: !!member.addressRefused
      };
      this.lastLookedUpPostalCode = this.billingForm.postalCode;
      // look up the city on open only, without overwriting a city the member already has
      this.prefillCityFromPostalCode(this.billingForm.postalCode, { prefillSingle: !this.billingForm.city });
      // after the opening, if the reassignment didn't change the postalCode value : disable
      // for the next tick to avoid deleting a legetimate change initiated by the user
      this.$nextTick(() => { this.lastLookedUpPostalCode = null; });
      // drop any error left from a previous opening
      this.$nextTick(() => this.$refs.formRef?.clearValidate());
    },
    onCancel () {
      this.$emit('cancel');
    },
    async onConfirm () {
      if (this.saving) return;
      // display errors and block if any field is invalid
      this.submitted = true;
      await this.$nextTick();
      const isFormValid = await this.$refs.formRef.validate().catch(() => false);
      if (!isFormValid) return;
      this.saving = true;
      try {
        await this.$services.HTTP.user.updateDomainUserInfo({
          postalCode: this.billingForm.postalCode,
          city: this.billingForm.city,
          country: this.billingForm.country,
          adr1: this.billingForm.adr1,
          ...(this.member?.adr2 ? { adr2: this.billingForm.adr2 } : {}),
          ...(this.member?.adr3 ? { adr3: this.billingForm.adr3 } : {}),
          addressRefused: this.member?.canRefuseAddress ? this.billingForm.addressRefused : false
        });
        // reload the logged member so node-api recomputes billingAddressMissingFields
        await this.$store.dispatch('user/loadDomainUserInfo');
        this.$emit('saved');
      } finally {
        this.saving = false;
      }
    },
    // query the national address API for address suggestions
    async queryAddress (query, callback) {
      if (!query || query.trim().length < 3) {
        callback([]);
        return;
      }
      try {
        const { data } = await this.$services.HTTP.zone.searchAddress(query, this.billingForm.postalCode || '');
        callback(data || []);
      } catch (e) {
        callback([]);
      }
    },
    // picking a suggestion overwrites the whole address block (adr1 handled by el-autocomplete value-key)
    onSelectAddress (suggestion) {
      this.billingForm.adr1 = suggestion.adr1 || '';
      this.billingForm.postalCode = suggestion.postalCode || '';
      this.billingForm.city = suggestion.city || '';
      this.billingForm.country = suggestion.country || '';
    },
    // postal code -> city mapping. 1 match -> prefill (unless prefillSingle is false) / multiple -> dropdown
    async prefillCityFromPostalCode (postalCode, { prefillSingle = true } = {}) {
      const code = (postalCode || '').trim();
      this.cityOptions = [];
      if (code.length < 5) return;
      try {
        const { data } = await this.$services.HTTP.zipCode.getCommunes(code);
        const communes = (data || []).map((commune) => commune.nomCommune).filter(Boolean);
        if (communes.length === 1) {
          if (prefillSingle) this.billingForm.city = communes[0];
        } else if (communes.length > 1) {
          // keep the member's existing city selectable even if it's not part of the lookup results
          if (this.billingForm.city && !communes.includes(this.billingForm.city)) communes.push(this.billingForm.city);
          this.cityOptions = communes;
        }
      } catch (e) {
        // lint doesn't like empty brackets : ignore failures (just a NTH item)
      }
    }
  }
};
</script>

<style scoped>
.nf525-banner {
  @apply flex flex-col gap-xs rounded p-sm text-left text-sm;
  background: var(--ariane-neutral-100, #f2f2f2);
}
.bloc-city {
  @apply flex gap-default;
}
.bloc-city__field {
  @apply flex-1 min-w-0 mb-md;
}
@media screen and (max-width: 767px) {
  .bloc-city {
    flex-direction: column;
    gap: 0;
  }
}
</style>
